import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub, FaEnvelope, FaGraduationCap, FaArrowRight, FaExternalLinkAlt, FaTrophy } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FiSun, FiMoon, FiMenu, FiX } from 'react-icons/fi';
import { Link } from 'react-scroll';
import MohamedImage from './assets/images/Mohamed.jpg';
import HomeGenAI from './assets/images/homegenai.png';
import XRCupid from './assets/images/xrcupid.png'
import ArticulAIte from './assets/images/ArticulAIte.png'
import GraphCatalyst from './assets/images/graphcatalyst.png'
import PCA from './assets/images/pca.png'
import cv from './assets/pdfs/Mohamed_Abdelnaby_CV.pdf'


const lightTheme = {
  body: '#fafbfc',
  text: '#0a0e1a',
  textLight: '#586073',
  primary: '#0a0e1a',
  secondary: '#3d4458',
  accent: '#5b6bf0',
  accentHover: '#4858d8',
  accentSecondary: '#8b5fe8',
  gradient: 'linear-gradient(135deg, #5b6bf0 0%, #8b5fe8 100%)',
  gradientText: 'linear-gradient(135deg, #5b6bf0 0%, #a570ff 100%)',
  sectionBg1: '#ffffff',
  sectionBg2: '#f5f7fb',
  card: '#ffffff',
  cardHover: '#f5f7fb',
  boxShadow: '0 1px 3px rgba(10, 14, 26, 0.06), 0 1px 2px rgba(10, 14, 26, 0.04)',
  boxShadowHover: '0 20px 60px -10px rgba(91, 107, 240, 0.18), 0 2px 6px rgba(10, 14, 26, 0.04)',
  border: '#e3e6ee',
  borderLight: '#eef0f6',
  hover: '#f5f7fb',
  success: '#00c853',
  warning: '#ff6f00',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBlur: 'blur(10px)'
};

const darkTheme = {
  body: '#0a0e1a',
  text: '#ffffff',
  textLight: '#a8b1cf',
  primary: '#ffffff',
  secondary: '#c5cce0',
  accent: '#7c8eff',
  accentHover: '#9ba8ff',
  accentSecondary: '#a78bfa',
  gradient: 'linear-gradient(135deg, #7c8eff 0%, #a78bfa 100%)',
  gradientText: 'linear-gradient(135deg, #7c8eff 0%, #c4a8ff 100%)',
  sectionBg1: '#0f1320',
  sectionBg2: '#0a0e1a',
  card: '#141828',
  cardHover: '#1c2138',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.15)',
  boxShadowHover: '0 20px 60px -10px rgba(124, 142, 255, 0.25), 0 2px 6px rgba(0, 0, 0, 0.2)',
  border: '#252b40',
  borderLight: '#1c2138',
  hover: '#1c2138',
  success: '#00e676',
  warning: '#ffb74d',
  glassBg: 'rgba(20, 24, 40, 0.7)',
  glassBlur: 'blur(10px)'
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :root {
    --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  body {
    font-family: var(--font-sans);
    line-height: 1.6;
    color: ${({ theme }) => theme.text};
    background-color: ${({ theme }) => theme.body};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  ::selection {
    background: ${({ theme }) => theme.accent};
    color: white;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.primary};
  }

  p {
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text};
    line-height: 1.7;
  }

  .gradient-text {
    background: ${({ theme }) => theme.gradientText};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  /* Accessibility: Focus visible styles */
  *:focus-visible {
    outline: 3px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
    border-radius: 2px;
  }

  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Accessibility: respect motion preferences globally */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Accessibility: Screen reader only utility */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Accessibility: Skip navigation link */
  .skip-nav {
    position: absolute;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.accent};
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0 0 8px 8px;
    font-weight: 600;
    font-size: 0.95rem;
    z-index: 10000;
    transition: top 0.2s ease;
    text-decoration: none;

    &:focus {
      top: 0;
      outline: 3px solid ${({ theme }) => theme.accent};
      outline-offset: 2px;
    }
  }

  /* Accessibility: Reduced motion preference */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;


const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.glassBg};
  backdrop-filter: ${({ theme }) => theme.glassBlur};
  -webkit-backdrop-filter: ${({ theme }) => theme.glassBlur};
  border-bottom: 1px solid ${({ theme }) => theme.borderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.scrolled {
    padding: 0.75rem 2rem;
    box-shadow: ${({ theme }) => theme.boxShadow};
  }

  .logo {
    font-size: 1.25rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.primary};
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateX(5px);
    }
  }

  .navbar {
    display: flex;
    align-items: center;
    gap: 2.5rem;

    a {
      cursor: pointer;
      font-size: 0.95rem;
      font-weight: 500;
      color: ${({ theme }) => theme.textLight};
      transition: all 0.3s ease;
      position: relative;
      padding: 0.5rem 0;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 0;
        height: 2px;
        background: ${({ theme }) => theme.accent};
        transition: width 0.3s ease;
      }

      &:hover {
        color: ${({ theme }) => theme.primary};
        
        &::after {
          width: 100%;
        }
      }

      &.active {
        color: ${({ theme }) => theme.accent};
        
        &::after {
          width: 100%;
        }
      }
    }
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .theme-toggle {
    cursor: pointer;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.text};
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      background: ${({ theme }) => theme.hover};
      border-color: ${({ theme }) => theme.accent};
    }
  }

  .mobile-menu-icon {
    display: none;
    cursor: pointer;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.text};
    background: none;
    border: none;
    padding: 0.5rem;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    padding: 1rem;

    .navbar {
      display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      flex-direction: column;
      gap: 0.5rem;
      background: ${({ theme }) => theme.glassBg};
      backdrop-filter: ${({ theme }) => theme.glassBlur};
      padding: 1.5rem;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      box-shadow: ${({ theme }) => theme.boxShadow};
    }

    .mobile-menu-icon {
      display: flex;
    }
  }
`;

function Header({ toggleTheme, theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderContainer isOpen={isOpen} className={scrolled ? 'scrolled' : ''}>
      <motion.div 
        className="logo"
        initial={{ x: -20 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.5 }}
      >
       <span className="gradient-text">Mohamed</span> Abdelnaby
      </motion.div>
      
      <button 
        className="mobile-menu-icon" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="main-navigation"
      >
        {isOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
      </button>

      <nav className="navbar" id="main-navigation" aria-label="Main navigation">
        <Link to="home" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Home</Link>
        <Link to="publications" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Publications</Link>
        <Link to="research-projects" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Research</Link>
        <Link to="projects" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Projects</Link>
        <Link to="skills" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Skills</Link>
        <Link to="about" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">About</Link>
        <Link to="awards" smooth duration={500} onClick={() => setIsOpen(false)} tabIndex="0" role="link">Awards</Link>
      </nav>

      <div className="controls">
        <motion.button 
          className="theme-toggle" 
          onClick={toggleTheme}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
        >
          {theme === 'light' ? <FiMoon aria-hidden="true" /> : <FiSun aria-hidden="true" />}
        </motion.button>
      </div>
    </HeaderContainer>
  );
}


const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 2rem 4rem;
  background: ${({ theme }) => theme.body};
  position: relative;
  overflow: hidden;

  /* Ambient mesh-gradient orbs: blue (top-right) + purple (bottom-left).
     Slow drift gives depth without distracting. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    will-change: transform;
  }

  &::before {
    top: -20%;
    right: -10%;
    width: 60vw;
    height: 60vw;
    max-width: 800px;
    max-height: 800px;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}33 0%, transparent 65%);
    animation: ${float} 22s ease-in-out infinite;
  }

  &::after {
    bottom: -25%;
    left: -15%;
    width: 55vw;
    height: 55vw;
    max-width: 700px;
    max-height: 700px;
    background: radial-gradient(circle, ${({ theme }) => theme.accentSecondary}26 0%, transparent 65%);
    animation: ${float} 26s ease-in-out infinite reverse;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none;
    }
  }

  .hero-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 350px;
    gap: 4rem;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  .hero-content {
    h1 {
      font-size: clamp(2.5rem, 6vw, 4rem);
      font-weight: 800;
      margin-bottom: 1.5rem;
      color: ${({ theme }) => theme.primary};
      line-height: 1.1;
      letter-spacing: -0.03em;
      animation: ${fadeInUp} 0.8s ease-out;
    }

    .subtitle {
      font-size: clamp(1.25rem, 3vw, 1.5rem);
      margin-bottom: 1rem;
      font-weight: 600;
      color: ${({ theme }) => theme.accent};
      animation: ${fadeInUp} 0.8s ease-out 0.1s both;
    }

    .description {
      font-size: clamp(1rem, 2.5vw, 1.125rem);
      color: ${({ theme }) => theme.textLight};
      margin-bottom: 2rem;
      line-height: 1.8;
      animation: ${fadeInUp} 0.8s ease-out 0.2s both;
    }

    .affiliation {
      display: inline-flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 2.5rem;
      padding: 0.75rem 1.25rem;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 50px;
      font-size: clamp(0.9rem, 2vw, 1rem);
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      animation: ${fadeInUp} 0.8s ease-out 0.3s both;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => theme.boxShadow};
        border-color: ${({ theme }) => theme.accent};
      }

      svg {
        color: ${({ theme }) => theme.accent};
      }
    }

    .social-links {
      display: flex;
      gap: 1rem;
      margin-bottom: 2.5rem;
      animation: ${fadeInUp} 0.8s ease-out 0.4s both;

      a {
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
        color: ${({ theme }) => theme.text};
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          color: white;
          background: ${({ theme }) => theme.accent};
          transform: translateY(-3px);
          box-shadow: ${({ theme }) => theme.boxShadowHover};
          border-color: transparent;
        }
      }
    }

    .cta-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      animation: ${fadeInUp} 0.8s ease-out 0.5s both;

      .cv-button {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 2rem;
        background: ${({ theme }) => theme.gradient};
        color: white;
        border-radius: 12px;
        font-weight: 600;
        font-size: 1rem;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        box-shadow: 0 4px 18px -2px ${({ theme }) => theme.accent}40,
                    0 1px 2px rgba(0, 0, 0, 0.1);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px -4px ${({ theme }) => theme.accent}66,
                      0 2px 4px rgba(0, 0, 0, 0.12);
        }

        &:focus-visible {
          outline: 2px solid ${({ theme }) => theme.accent};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          &:hover { transform: none; }
        }

        svg {
          transition: transform 0.3s ease;
        }

        &:hover svg {
          transform: translateX(3px);
        }
      }

      .contact-button {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 2rem;
        background: transparent;
        color: ${({ theme }) => theme.text};
        border: 2px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        font-weight: 600;
        font-size: 1rem;
        transition: all 0.3s ease;

        &:hover {
          background: ${({ theme }) => theme.card};
          border-color: ${({ theme }) => theme.accent};
          transform: translateY(-2px);
        }
      }
    }
  }

  .hero-image {
    display: flex;
    justify-content: center;
    animation: ${fadeInUp} 1s ease-out 0.3s both;

    .image-wrapper {
      position: relative;
      
      .image-container {
        position: relative;
        width: clamp(250px, 30vw, 320px);
        height: clamp(250px, 30vw, 320px);
        border-radius: 30px;
        overflow: hidden;
        background: ${({ theme }) => theme.gradient};
        box-shadow: 0 20px 60px -10px ${({ theme }) => theme.accent}40;

        &::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: ${({ theme }) => theme.gradient};
          border-radius: 30px;
          z-index: -1;
          opacity: 0.5;
          filter: blur(20px);
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .decoration {
        position: absolute;
        width: 100%;
        height: 100%;
        border: 2px solid ${({ theme }) => theme.accent}20;
        border-radius: 30px;
        top: 20px;
        left: 20px;
        z-index: -1;
      }
    }
  }

  @media (max-width: 1024px) {
    .hero-container {
      grid-template-columns: 1fr;
      text-align: center;
      gap: 3rem;
      max-width: 700px;
    }

    .hero-content {
      .affiliation {
        margin-left: auto;
        margin-right: auto;
      }

      .social-links,
      .cta-group {
        justify-content: center;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem 3rem;
    min-height: 85vh;
  }
`;

function Hero() {
  return (
    <HeroSection id="home">
      <div className="hero-container">
        <div className="hero-content">
          <h1>Mohamed <span className="gradient-text">Abdelnaby</span></h1>
          <div className="subtitle">CS PhD Student</div>
          <div className="affiliation">
            <FaGraduationCap aria-hidden="true" />
            <span>Virginia Tech | Computer Science</span>
          </div>
          <p className="description">
            Passionate researcher at the intersection of High-Performance Computing, Machine Learning, and Scientific Computing,
            focused on developing scalable computational frameworks that accelerate scientific discovery and unlock insights from massive datasets.
          </p>
          
          <div className="social-links">
            <motion.a 
              href="https://www.linkedin.com/in/mabdelnaby1/" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="LinkedIn profile (opens in new tab)"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaLinkedin aria-hidden="true" />
            </motion.a>
            <motion.a 
              href="https://github.com/MohamedHAbdelnaby" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="GitHub profile (opens in new tab)"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaGithub aria-hidden="true" />
            </motion.a>
            <motion.a 
              href="https://x.com/iam_abdelnaby" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="X profile (opens in new tab)"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaXTwitter aria-hidden="true" />
            </motion.a>
            <motion.a 
              href="mailto:mohamedabdelnaby@vt.edu"
              aria-label="Send email to mohamedabdelnaby@vt.edu"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaEnvelope aria-hidden="true" />
            </motion.a>
          </div>

          <div className="cta-group">
            <a
              href={cv}
              className="cv-button"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download CV (opens PDF in new tab)"
              onClick={() => {
                if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                  window.gtag('event', 'cv_download', {
                    event_category: 'engagement',
                    event_label: 'Portfolio Hero CV',
                    transport_type: 'beacon'
                  });
                }
              }}
            >
              Download CV <FaArrowRight aria-hidden="true" />
            </a>
            <Link to="projects" smooth duration={500} className="contact-button">
              View Projects
            </Link>
          </div>
        </div>

        <div className="hero-image">
          <div className="image-wrapper">
            <div className="image-container">
              <img 
                src={MohamedImage}
                alt="Mohamed Abdelnaby"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div className="decoration" />
          </div>
        </div>
      </div>
    </HeroSection>
  );
}




const NewsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 1000px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .news-timeline {
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 60px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: ${({ theme }) => theme.border};
    }
  }

  .news-item {
    position: relative;
    padding-left: 120px;
    margin-bottom: 2.5rem;
    
    &::before {
      content: '';
      position: absolute;
      left: 52px;
      top: 8px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${({ theme }) => theme.accent};
      border: 3px solid ${({ theme }) => theme.body};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.accent}20;
    }

    .date {
      position: absolute;
      left: 0;
      top: 5px;
      font-size: 0.75rem;
      font-weight: 700;
      color: ${({ theme }) => theme.accent};
      text-transform: uppercase;
      letter-spacing: 0.05em;
      width: 50px;
    }

    .news-card {
      background: ${({ theme }) => theme.card};
      padding: 1.75rem;
      border-radius: 16px;
      border: 1px solid ${({ theme }) => theme.borderLight};
      transition: all 0.3s ease;
      cursor: default;

      &:hover {
        transform: translateX(5px);
        box-shadow: ${({ theme }) => theme.boxShadowHover};
        border-color: ${({ theme }) => theme.accent}30;
      }

      .news-title {
        font-size: 1.125rem;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        margin-bottom: 0.75rem;
        line-height: 1.4;
      }

      .news-description {
        color: ${({ theme }) => theme.textLight};
        line-height: 1.6;
        font-size: 0.95rem;
      }

      .news-card-footer {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-top: 1rem;
        flex-wrap: wrap;
      }

      .news-tag {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        background: ${({ theme }) => theme.accent}15;
        color: ${({ theme }) => theme.accent};
        border-radius: 20px;
        font-size: 0.75rem;
        font-weight: 600;
      }

      .news-paper-link {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: ${({ theme }) => theme.accent};
        text-decoration: none;
        border: 1px solid ${({ theme }) => theme.accent}50;
        border-radius: 20px;
        padding: 0.25rem 0.75rem;
        transition: background 0.2s, color 0.2s;

        &:hover {
          background: ${({ theme }) => theme.accent};
          color: #fff;
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    
    .news-timeline::before {
      left: 20px;
    }
    
    .news-item {
      padding-left: 50px;
      
      &::before {
        left: 12px;
      }
      
      .date {
        position: relative;
        left: auto;
        margin-bottom: 0.5rem;
        width: auto;
      }
    }
  }
`;

function News() {
  const newsItems = [
    {
      date: 'Apr 2026',
      title: 'First Author Paper Published in Journal of Computational Biology!',
      description: 'Our paper "Random Projection Methods Outperform Principal Component Analysis for Dimensionality Reduction in Single Cell RNA-Seq" is now officially published in the Journal of Computational Biology. Read it at doi.org/10.1177/15578666261436821',
      tag: 'Publication',
      link: 'https://doi.org/10.1177/15578666261436821'
    },
    {
      date: 'Feb 2026',
      title: 'First Author Paper Accepted at JCB (Journal of Computational Biology)',
      description: 'Our paper titled "Random Projection Methods Outperform Principal Component Analysis for Dimensionality Reduction in Single Cell RNA-Seq" got accepted for publication at Journal of Computational Biology',
      tag: 'Publication'
    },
    {
      date: 'Aug 2025',
      title: 'Started PhD at Virginia Tech',
      description: 'Excited to begin my PhD journey in Computer Science at Virginia Tech, focusing on High Performance Computing and Machine Learning/AI research.',
      tag: 'Education'
    },
    {
      date: 'May 2025',
      title: 'Graduated with MS in Computer Science',
      description: 'Successfully completed my Master\'s degree in Computer Science at the University of Oklahoma with focus on applied AI/ML.',
      tag: 'Achievement'
    },
    {
      date: 'Jan 2025',
      title: 'Won MIT Reality Hacks 2025',
      description: 'Achieved second place at MIT Reality Hacks Founders Lab Track, the world\'s largest XR hackathons.',
      tag: 'Achievement'
    },
    {
      date: 'Jan 2025',
      title: 'Paper Presented at ICCABS 2025',
      description: 'Presented research on benchmarking Random Projections and PCA for downstream analysis tasks in single-cell RNA sequencing. Paper accepted in Springer Lecture Notes in Bioinformatics proceedings.',
      tag: 'Publication'
    },
    {
      date: 'Nov 2024',
      title: 'Won at Stanford Immerse the Bay 2024 Hackathon',
      description: 'Our project, HomeGen.AI, was one of the winning projects at Stanford.',
      tag: 'Achievement'
    },
    {
      date: 'Jul 2024',
      title: 'Poster Presented at ISMB 2024',
      description: 'Presented poster on dimensionality reduction methods for single cell RNA sequencing data at ISMB 2024.',
      tag: 'Conference'
    },
    {
      date: 'Apr 2024',
      title: 'Presented at RECOMB 2024',
      description: 'Presented our research on random projections for high-dimensional single cell RNA-seq data at RECOMB 2024.',
      tag: 'Conference'
    }
  ];

  return (
    <NewsSection id="news">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Recent <span>Updates</span>
        </motion.h2>

        <div className="news-timeline">
          {newsItems.map((item, index) => (
            <motion.div 
              key={index} 
              className="news-item"
              initial={{ x: -30 }}
              whileInView={{ x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="date">{item.date}</div>
              <div className="news-card">
                <div className="news-title">{item.title}</div>
                <div className="news-description">{item.description}</div>
                <div className="news-card-footer">
                  <span className="news-tag">{item.tag}</span>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="news-paper-link" aria-label={`Read paper: ${item.title} (opens in new tab)`}>
                      <FaExternalLinkAlt aria-hidden="true" /> Read Paper
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </NewsSection>
  );
}


const AboutSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .about-content {
    max-width: 900px;
    margin: 0 auto 4rem;

    p {
      font-size: 1.0625rem;
      line-height: 1.8;
      color: ${({ theme }) => theme.textLight};
      margin-bottom: 1.5rem;
      
      &:first-child {
        font-size: 1.125rem;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
      }
    }

    .highlight {
      color: ${({ theme }) => theme.accent};
      font-weight: 600;
    }
  }

  .education {
    margin-top: 4rem;

    h3 {
      font-size: 2rem;
      margin-bottom: 2rem;
      text-align: center;
      font-weight: 700;
    }

    .edu-timeline {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      max-width: 800px;
      margin: 0 auto;
    }

    .edu-item {
      background: ${({ theme }) => theme.card};
      padding: 2rem;
      border-radius: 20px;
      border: 1px solid ${({ theme }) => theme.borderLight};
      display: flex;
      gap: 2rem;
      align-items: flex-start;
      transition: all 0.3s ease;
      position: relative;

      &:hover {
        transform: translateX(10px);
        box-shadow: ${({ theme }) => theme.boxShadowHover};
      }

      .edu-icon {
        width: 50px;
        height: 50px;
        background: ${({ theme }) => theme.gradient};
        border-radius: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        
        svg {
          color: white;
          font-size: 1.5rem;
        }
      }

      .edu-content {
        flex: 1;

        .degree {
          font-size: 1.25rem;
          font-weight: 700;
          color: ${({ theme }) => theme.primary};
          margin-bottom: 0.5rem;
        }

        .institution {
          color: ${({ theme }) => theme.accent};
          font-weight: 500;
          margin-bottom: 0.25rem;
        }

        .year {
          color: ${({ theme }) => theme.textLight};
          font-size: 0.875rem;
        }
      }
    }
  }

  @media (max-width: 568px) {
    padding: 4rem 1rem;

    .edu-item {
      padding: 1.5rem;
      gap: 1.5rem;
    }
  }
`;

function About() {
  return (
    <AboutSection id="about">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          About <span className="gradient-text">Me</span>
        </motion.h2>
        
        <motion.div 
          className="about-content"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p>
            I'm a <span className="highlight">Computer Science PhD student at Virginia Tech</span>, working at the intersection of high-performance computing, machine learning, and scientific computing. My research focuses on developing scalable computational frameworks that accelerate scientific discovery and unlock insights from massive datasets.
          </p>

          <p>
            My research journey began as a freshman exploring adversarial machine learning, then evolved into computational biology where I worked on dimensionality reduction techniques for single-cell RNA sequencing analysis. Most recently, I've been developing protein language models for T-cell receptor analysis and building distributed training systems for biological data. Now, my work centers on creating efficient ML systems specifically designed for scientific computing applications.
          </p>

          <p>
            Beyond research, I'm passionate about education and mentorship. I run a tutoring platform where I've worked with students across machine learning, data science, and algorithms, helping them bridge the gap between theory and practice. I grew up in Cairo, Egypt, where my curiosity about how computers work sparked a journey that led me through a bachelor's in Computer Engineering, a master's in Computer Science, and now my PhD.
          </p>

          <p>
            Outside the lab, I'm a husband who values family above all else, a game developer who enjoys both playing and building games, and someone who finds balance through chess, traveling, and swimming. I'm also active in the hackathon community, having won at MIT Reality Hacks and Stanford Immerse the Bay, and I love the collaborative energy of building innovative solutions under pressure.
          </p>
        </motion.div>

        <motion.div 
          className="education"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h3>Education <span className="gradient-text">Journey</span></h3>
          <div className="edu-timeline">
            <div className="edu-item">
              <div className="edu-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="edu-content">
                <div className="degree">PhD in Computer Science</div>
                <div className="institution">Virginia Tech</div>
                <div className="year">2025 - Present</div>
              </div>
            </div>
            <div className="edu-item">
              <div className="edu-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="edu-content">
                <div className="degree">MS in Computer Science</div>
                <div className="institution">University of Oklahoma</div>
                <div className="year">2024 - 2025</div>
              </div>
            </div>
            <div className="edu-item">
              <div className="edu-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="edu-content">
                <div className="degree">BS in Computer Engineering</div>
                <div className="institution">University of Oklahoma</div>
                <div className="year">2020 - 2024</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AboutSection>
  );
}

const ExperienceSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};
  position: relative;
  overflow: hidden;

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .experience-timeline {
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 60px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: ${({ theme }) => theme.border};
    }
  }

  .experience-item {
    position: relative;
    padding-left: 140px;
    margin-bottom: 3rem;
    
    &::before {
      content: '';
      position: absolute;
      left: 52px;
      top: 12px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${({ theme }) => theme.accent};
      border: 3px solid ${({ theme }) => theme.body};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.accent}20;
    }

    .duration {
      position: absolute;
      left: 0;
      top: 8px;
      font-size: 0.75rem;
      font-weight: 700;
      color: ${({ theme }) => theme.accent};
      text-transform: uppercase;
      letter-spacing: 0.05em;
      width: 50px;
      text-align: center;
    }

    .experience-card {
      background: ${({ theme }) => theme.card};
      padding: 2.5rem;
      border-radius: 20px;
      border: 1px solid ${({ theme }) => theme.borderLight};
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${({ theme }) => theme.gradient};
        opacity: 0;
        transition: opacity 0.4s ease;
        z-index: 0;
      }

      &:hover {
        transform: translateX(10px);
        box-shadow: ${({ theme }) => theme.boxShadowHover};
        border-color: ${({ theme }) => theme.accent}30;

        &::before {
          opacity: 0.03;
        }

        .company-logo {
          transform: scale(1.05);
        }
      }

      .experience-header {
        display: flex;
        align-items: flex-start;
        gap: 2rem;
        margin-bottom: 1.5rem;
        position: relative;
        z-index: 1;

        .company-logo {
          width: 60px;
          height: 60px;
          background: ${({ theme }) => theme.gradient};
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 1.5rem;
          color: white;
          font-weight: 800;
          transition: transform 0.3s ease;
        }

        .role-info {
          flex: 1;

          .role-title {
            font-size: 1.375rem;
            font-weight: 700;
            color: ${({ theme }) => theme.primary};
            margin-bottom: 0.5rem;
            line-height: 1.3;
          }

          .company-name {
            color: ${({ theme }) => theme.accent};
            font-weight: 600;
            margin-bottom: 0.25rem;
          }

          .location {
            color: ${({ theme }) => theme.textLight};
            font-size: 0.875rem;
          }
        }
      }

      .experience-description {
        color: ${({ theme }) => theme.textLight};
        line-height: 1.7;
        margin-bottom: 1.5rem;
        position: relative;
        z-index: 1;
      }

      .achievements {
        position: relative;
        z-index: 1;

        .achievements-title {
          font-size: 1rem;
          font-weight: 600;
          color: ${({ theme }) => theme.primary};
          margin-bottom: 1rem;
        }

        ul {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            position: relative;
            padding-left: 1.5rem;
            margin-bottom: 0.75rem;
            color: ${({ theme }) => theme.textLight};
            line-height: 1.6;

            &::before {
              content: '▶';
              position: absolute;
              left: 0;
              color: ${({ theme }) => theme.accent};
              font-size: 0.75rem;
              top: 0.1rem;
            }
          }
        }
      }

      .skills-used {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 1.5rem;
        position: relative;
        z-index: 1;

        .skill-tag {
          padding: 0.375rem 0.875rem;
          background: ${({ theme }) => theme.hover};
          color: ${({ theme }) => theme.text};
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          border: 1px solid ${({ theme }) => theme.borderLight};
          transition: all 0.3s ease;

          &:hover {
            background: ${({ theme }) => theme.accent}15;
            border-color: ${({ theme }) => theme.accent};
            color: ${({ theme }) => theme.accent};
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    
    .experience-timeline::before {
      left: 20px;
    }
    
    .experience-item {
      padding-left: 60px;
      
      &::before {
        left: 12px;
      }
      
      .duration {
        position: relative;
        left: auto;
        margin-bottom: 1rem;
        width: auto;
        text-align: left;
      }

      .experience-card {
        padding: 2rem;

        .experience-header {
          flex-direction: column;
          gap: 1.5rem;

          .company-logo {
            align-self: flex-start;
          }
        }
      }
    }
  }
`;

function Experience() {
  const experiences = [
    {
      duration: 'Jan 2026\nPresent',
      title: 'Graduate Teaching Assistant',
      company: 'Virginia Tech',
      location: 'Blacksburg, VA',
      logo: 'VT',
      description: 'Supporting 80 undergraduate students in CS 3754 (Cloud Software Engineering), covering cloud architecture, distributed systems, and modern software deployment practices.',
      achievements: [
        'Designed and implemented autograder infrastructure for programming assignments, automating evaluation workflows and ensuring consistent, objective grading across all student submissions',
        'Developed grading rubrics in collaboration with course faculty, establishing clear assessment criteria for both coding assignments and cloud deployment projects',
        'Conduct weekly office hours to assist students with cloud development concepts, debugging, and project architecture decisions'
      ],
      skills: ['Cloud Architecture', 'Distributed Systems', 'Autograding', 'AWS/GCP', 'Mentorship']
    },
    {
      duration: 'Aug 2025\nDec 2025',
      title: 'Graduate Teaching Assistant',
      company: 'Virginia Tech',
      location: 'Blacksburg, VA',
      logo: 'VT',
      description: 'Served as Graduate Teaching Assistant for CS 5704 (Software Engineering), supporting graduate students in advanced software engineering practices and industry-aligned technical proficiency.',
      achievements: [
        'Supported over 20 graduate students in mastering Agile methodology, DevOps workflows, and software lifecycle management',
        'Conducted weekly office hours for hands-on development in GitHub, CI/CD pipelines, Docker containerization, and project design',
        'Collaborated with faculty on grading rubrics and project evaluation for team collaboration, system architecture, and design patterns',
        'Provided structured feedback to enhance students\' real-world readiness and technical capabilities'
      ],
      skills: ['Software Engineering', 'Agile/DevOps', 'CI/CD', 'Docker', 'Mentorship']
    },
    {
      duration: 'May 2025\nPresent',
      title: 'Computer Science Tutor',
      company: 'Self-Employed',
      location: 'Remote',
      logo: 'CS',
      description: 'Delivering personalized instruction to students across computer science and data-focused subjects, with 1,400+ hours of teaching and consistently high satisfaction ratings.',
      achievements: [
        'Delivered 1,400+ hours of instruction to 100+ students across computer science and data-focused subjects',
        'Built and launched a dedicated tutoring website to streamline outreach, scheduling, and student intake',
        'Designed structured lesson plans that connect theory with hands-on projects using Python, TensorFlow, and scikit-learn'
      ],
      skills: ['Machine Learning', 'Data Science', 'Python', 'TensorFlow', 'Curriculum Design']
    },
    {
      duration: 'May 2025\nAug 2025',
      title: 'Summer Research Intern',
      company: 'University of Oklahoma',
      location: 'Norman, OK',
      logo: 'OU',
      description: 'Advanced research in Protein Large Language Models for T-Cell Receptor specificity and phenotype prediction, with poster submission to ACM-BCB 2025.',
      achievements: [
        'Benchmarked state-of-the-art models including ESM (Facebook AI), ProtBERT, and ProtT5 across multiple configurations',
        'Designed large-scale experimental pipelines for biological sequence understanding and protein analysis',
        'Leveraged HPC clusters for distributed training, GPU-accelerated inference, and parallelized model optimization',
        'Significantly improved scalability, training efficiency, and predictive accuracy through advanced computational methods'
      ],
      skills: ['Protein LLMs', 'HPC', 'Deep Learning', 'Bioinformatics', 'GPU Computing']
    },
    {
      duration: 'Jul 2024\nMay2025',
      title: 'Graduate Research Assistant',
      company: 'University of Oklahoma',
      location: 'Norman, OK',
      logo: 'OU',
      description: 'Leading advanced research in bioinformatics and computational biology, focusing on single-cell RNA sequencing analysis and dimensionality reduction techniques.',
      achievements: [
        'Developed optimized dimensionality reduction and clustering pipelines for single-cell RNA sequencing data',
        'Benchmarked PCA and Random Projection methods on high-dimensional datasets with parallelization on multi-CPU systems',
        'Presented research at RECOMB 2024 and ISMB 2024, with paper accepted at ICCABS 2025',
        'Achieved significant improvements in clustering accuracy and large-scale data processing workflows'
      ],
      skills: ['Bioinformatics', 'Single-cell RNA-seq', 'PCA', 'Random Projections', 'Parallel Computing']
    },
    {
      duration: 'May 2024\nPresent',
      title: 'Technology Director',
      company: 'Hacklahoma',
      location: 'Norman, OK',
      logo: 'HK',
      description: 'Promoted to lead technology initiatives for Oklahoma\'s largest hackathon, managing development teams and driving innovation in event technology.',
      achievements: [
        'Leading a team of 4 software developers in creating the Hacklahoma 2025 website',
        'Targeting 30% increase in user engagement through enhanced platform features',
        'Integrating 3 key features projected to enhance user interaction by 15%',
        'Streamlining event management processes to improve overall hackathon efficiency'
      ],
      skills: ['Team Leadership', 'Web Development', 'Project Management', 'Event Technology']
    },
    {
      duration: 'Aug 2023\nMay 2024',
      title: 'Vice President Internal',
      company: 'OU UI/UX Design Club',
      location: 'Norman, OK',
      logo: 'UX',
      description: 'Led internal operations for the UI/UX Design Club, organizing high-impact events and coordinating academic research initiatives.',
      achievements: [
        'Hosted events with industry-leading speakers from Microsoft, Paycom, and IBM for 100+ members',
        'Led conference submissions for ACM-CHI, coordinating acceptance of 3 workshop papers',
        'Organized workshop on Inclusive AR focusing on accessible augmented reality experiences',
        'Enhanced learning experience and professional development opportunities for club members'
      ],
      skills: ['Event Management', 'Academic Coordination', 'UI/UX Design', 'Industry Relations']
    },
    {
      duration: 'May 2023\nMay 2024',
      title: 'Software Developer',
      company: 'Hacklahoma',
      location: 'Norman, OK',
      logo: 'HK',
      description: 'Developed and maintained the official Hacklahoma website and registration platform, serving thousands of participants and stakeholders.',
      achievements: [
        'Managed full-cycle development of Hacklahoma 2024 website for 1,000+ users',
        'Created interactive and user-friendly interface for organization and event information',
        'Engineered online registration system optimizing data collection for 300+ students',
        'Boosted participant registration and engagement by 20% through streamlined sign-up process'
      ],
      skills: ['Full-stack Development', 'User Experience', 'Database Design', 'Web Optimization']
    },
    {
      duration: 'May 2022\nMay 2023',
      title: 'Project Manager',
      company: 'OU Artificial Intelligence Organization',
      location: 'Norman, OK',
      logo: 'AI',
      description: 'Managed technical projects and educational initiatives for the university\'s AI organization, fostering learning and innovation in artificial intelligence.',
      achievements: [
        'Led technical projects including workshops, seminars, and competitions for 100+ members',
        'Organized OU AI Symposium facilitating learning and collaboration among students',
        'Coordinated 100+ participant events featuring talks on ML, NLP, and computer vision',
        'Focused on practical applications of machine learning and AI technologies'
      ],
      skills: ['Project Management', 'AI/ML Education', 'Event Coordination', 'Technical Leadership']
    }
  ];

  return (
    <ExperienceSection id="experience">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Professional <span>Experience</span>
        </motion.h2>

        <div className="experience-timeline">
          {experiences.map((exp, index) => (
            <motion.div 
              key={index} 
              className="experience-item"
              initial={{ x: -30 }}
              whileInView={{ x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="duration">{exp.duration}</div>
              <div className="experience-card">
                <div className="experience-header">
                  <div className="company-logo">{exp.logo}</div>
                  <div className="role-info">
                    <div className="role-title">{exp.title}</div>
                    <div className="company-name">{exp.company}</div>
                    <div className="location">{exp.location}</div>
                  </div>
                </div>
                <div className="experience-description">{exp.description}</div>
                <div className="achievements">
                  <div className="achievements-title">Key Achievements:</div>
                  <ul>
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex}>{achievement}</li>
                    ))}
                  </ul>
                </div>
                <div className="skills-used">
                  {exp.skills.map((skill, skillIndex) => (
                    <span key={skillIndex} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </ExperienceSection>
  );
}


const ResearchSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}05 0%, transparent 70%);
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .research-areas {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
  }

  .research-card {
    background: ${({ theme }) => theme.card};
    padding: 2.5rem;
    border-radius: 24px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    position: relative;
    overflow: hidden;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s ease;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: ${({ theme }) => theme.gradient};
      opacity: 0;
      transition: opacity 0.4s ease;
      z-index: 0;
    }

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;

      &::before {
        opacity: 0.07;
      }

      h3 {
        background: ${({ theme }) => theme.gradientText};
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }

    h3 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
      color: ${({ theme }) => theme.primary};
      position: relative;
      z-index: 1;
      transition: color 0.3s ease;
    }

    p {
      color: ${({ theme }) => theme.textLight};
      line-height: 1.7;
      position: relative;
      z-index: 1;
    }

    .research-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 1.5rem;
      position: relative;
      z-index: 1;

      .tag {
        padding: 0.25rem 0.75rem;
        background: ${({ theme }) => theme.hover};
        color: ${({ theme }) => theme.text};
        border-radius: 15px;
        font-size: 0.75rem;
        font-weight: 500;
        border: 1px solid ${({ theme }) => theme.borderLight};
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .research-areas {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`;

function Research() {
  const researchAreas = [
    {
      title: "High-Performance Computing",
      description: "Developing scalable HPC systems for computational biology, optimizing parallel algorithms for massive datasets, and creating efficient frameworks that bridge the gap between raw computational power and scientific discovery.",
      tags: ["Parallel Computing", "Scalability", "Performance Optimization"]
    },
    {
      title: "Machine Learning at Scale",
      description: "Designing distributed ML frameworks for scientific applications, exploring dimensionality reduction techniques for genomic data, and implementing large-scale neural networks on supercomputing infrastructure.",
      tags: ["Distributed Systems", "Deep Learning", "Scientific ML"]
    },
    {
      title: "Scientific Computing",
      description: "Developing computational frameworks for complex scientific simulations, building scalable pipelines for multi-domain research applications, and applying HPC to solve challenging problems across disciplines like biology, physics, and climate modeling.",
      tags: ["Scientific Simulation", "Multi-domain", "Computational Methods"]
    }
  ];

  return (
    <ResearchSection id="research">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Research <span>Interests</span>
        </motion.h2>

        <div className="research-areas">
          {researchAreas.map((area, index) => (
            <motion.div 
              key={index} 
              className="research-card"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3>{area.title}</h3>
              <p>{area.description}</p>
              <div className="research-tags">
                {area.tags.map((tag, tagIndex) => (
                  <span key={tagIndex} className="tag">{tag}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </ResearchSection>
  );
}


const ProjectsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 2rem;
  }

  .project-card {
    background: ${({ theme }) => theme.card};
    border-radius: 24px;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.borderLight};
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s ease;
    position: relative;
    cursor: pointer;

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;

      .project-image {
        img {
          transform: scale(1.05);
        }

        &::after {
          opacity: 0.7;
        }
      }

      .project-links a {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }

    .project-image {
      width: 100%;
      height: 220px;
      background: ${({ theme }) => theme.hover};
      position: relative;
      overflow: hidden;
      transition: transform 0.4s ease;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 100%);
        opacity: 0.5;
        transition: opacity 0.4s ease;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
      }

      .tech-stack {
        position: absolute;
        bottom: 1rem;
        left: 1rem;
        display: flex;
        gap: 0.5rem;
        z-index: 2;

        .tech {
          padding: 0.25rem 0.75rem;
          background: rgba(255, 255, 255, 0.9);
          color: #333;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
        }
      }
    }

    .project-content {
      padding: 2rem;

      .project-header {
        margin-bottom: 1rem;

        h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${({ theme }) => theme.primary};
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }

        .project-year {
          color: ${({ theme }) => theme.accent};
          font-size: 0.875rem;
          font-weight: 600;
        }

        
      }

      .project-description {
        color: ${({ theme }) => theme.textLight};
        line-height: 1.7;
        margin-bottom: 1.5rem;
      }

      .project-links {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;

        a {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: ${({ theme }) => theme.hover};
          color: ${({ theme }) => theme.text};
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
          transition: all 0.3s ease;
          border: 1px solid ${({ theme }) => theme.borderLight};

          &:hover {
            background: ${({ theme }) => theme.accent};
            color: white;
            border-color: ${({ theme }) => theme.accent};
            transform: translateY(-2px);
          }

          svg {
            font-size: 1rem;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .projects-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`;

function Projects() {
const projects = [
    {
    title: "XR Cupid (Winner | MIT Reality Hacks 2025)",
    year: "2025", 
    description: "VR dating coach app using personality-based AI coaching with real-time eye contact and posture tracking, designed specifically for neurodivergent users seeking dating guidance.",
    links: [
      { type: "github", url: "https://github.com/MohamedHAbdelnaby/XRCupid", label: "GitHub" },
      { type: "devpost", url: "https://devpost.com/software/xrcupid", label: "DevPost" }
    ],
    tech: ["Meta Quest 3", "Convai", "Unity"],
    image: XRCupid
  },
    {
    title: "HomeGen (Winner | Stanford Immerse the Bay 2024)",
    year: "2024",
    description: "Immersive 3D home visualization tool converting architectural floor plans into true-to-scale VR experiences, enabling clients to walk through homes before construction begins.",
    links: [
      { type: "github", url: "https://github.com/MohamedHAbdelnaby/HomeGen.ai", label: "GitHub" },
      { type: "devpost", url: "https://devpost.com/software/homegen", label: "DevPost" }
    ],
    tech: ["ShapesXR", "AWS", "3D Modeling"],
    image: HomeGenAI
  },
  {
    title: "ArticulAIte (Stanford Tree Hacks 2025)",
    year: "2025",
    description: "VR-enabled AI interview automation platform generating structured interviews with AI avatars, featuring automated question generation, customizable evaluation criteria, and transparent post-interview analysis.",
    links: [
      { type: "github", url: "https://github.com/MohamedHAbdelnaby/ArticulAIte", label: "GitHub" },
      { type: "devpost", url: "https://devpost.com/software/articuaite", label: "DevPost" }
    ],
    tech: ["Unity", "Meta Quest 3", "OpenAI"],
    image: ArticulAIte
  },
  {
    title: "GraphCatalyst (Building the Next-Gen Agentic App with GraphRAG & NVIDIA cuGraph)",
    year: "2025",
    description: "GPU-accelerated graph analytics platform for e-commerce networks with cuGraph integration, natural language querying, and real-time product recommendation insights.",
    links: [
      { type: "github", url: "https://github.com/MohamedHAbdelnaby/GraphCatalyst", label: "GitHub" },
      { type: "devpost", url: "https://devpost.com/software/graphcatalyst-6k5idm", label: "DevPost" }
    ],
    tech: ["cuGraph", "ArangoDB", "Streamlit"],
    image: GraphCatalyst
  }
];

  return (
    <ProjectsSection id="projects">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Featured <span className="gradient-text">Projects</span>
        </motion.h2>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <motion.div 
              key={index} 
              className="project-card"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
                <div className="tech-stack">
                  {project.tech.slice(0, 2).map((tech, techIndex) => (
                    <span key={techIndex} className="tech">{tech}</span>
                  ))}
                </div>
              </div>
              <div className="project-content">
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <div className="project-year">{project.year}</div>
                </div>
                <p className="project-description">{project.description}</p>
                <div className="project-links">
                  {project.links.map((link, linkIndex) => {

                    const getIcon = (type) => {
                      switch(type) {
                        case 'github': return <FaGithub />;
                        case 'demo': case 'website': return <FaExternalLinkAlt />;
                        case 'devpost': return <FaExternalLinkAlt />;
                        case 'paper': return <FaExternalLinkAlt />;
                        default: return <FaExternalLinkAlt />;
                      }
                    };

                    return (
                      <a 
                        key={linkIndex}
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {getIcon(link.type)} {link.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </ProjectsSection>
  );
}


const PublicationsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 1000px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .publications-list {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  .section-divider {
    margin: 2rem 0;
    
    h3 {
      font-size: 1.5rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 1.5rem;
      padding-bottom: 0.75rem;
      border-bottom: 2px solid ${({ theme }) => theme.border};
      
      span {
        color: ${({ theme }) => theme.accent};
      }
    }
  }

  .publication-card {
    background: ${({ theme }) => theme.card};
    padding: 2.5rem;
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${({ theme }) => theme.gradient};
      transform: scaleY(0);
      transition: transform 0.3s ease;
    }

    &.peer-reviewed {
      border: 2px solid ${({ theme }) => theme.accent}30;
      
      &::before {
        width: 5px;
      }
    }

    &.under-review {
      border: 2px solid ${({ theme }) => theme.warning}30;
      
      &::before {
        background: linear-gradient(135deg, ${({ theme }) => theme.warning} 0%, ${({ theme }) => theme.accent} 100%);
        width: 5px;
      }
    }

    &:hover {
      transform: translateX(8px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      
      &::before {
        transform: scaleY(1);
      }

      .venue-badge {
        background: ${({ theme }) => theme.gradient};
        color: white;
      }
    }

    .pub-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 2rem;
      margin-bottom: 1rem;

      .pub-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        line-height: 1.4;
        flex: 1;
      }

      .pub-year {
        background: ${({ theme }) => theme.accent}15;
        color: ${({ theme }) => theme.accent};
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 700;
        white-space: nowrap;
      }
    }

    .authors {
      color: ${({ theme }) => theme.text};
      margin-bottom: 1rem;
      font-weight: 500;
      
      .highlight-author {
        color: ${({ theme }) => theme.accent};
        font-weight: 700;
      }
    }

    .venue-info {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;

      .venue {
        color: ${({ theme }) => theme.textLight};
        font-style: italic;
        flex: 1;
      }

      .venue-badge {
        padding: 0.25rem 0.75rem;
        background: ${({ theme }) => theme.hover};
        color: ${({ theme }) => theme.text};
        border-radius: 15px;
        font-size: 0.75rem;
        font-weight: 600;
        transition: all 0.3s ease;
        
        &.peer-reviewed {
          background: ${({ theme }) => theme.success}20;
          color: ${({ theme }) => theme.success};
          border: 1px solid ${({ theme }) => theme.success}40;
        }
        
        &.under-review {
          background: ${({ theme }) => theme.warning}20;
          color: ${({ theme }) => theme.warning};
          border: 1px solid ${({ theme }) => theme.warning}40;
        }
        
        &.position-paper {
          background: ${({ theme }) => theme.accent}15;
          color: ${({ theme }) => theme.accent};
          border: 1px solid ${({ theme }) => theme.accent}30;
        }
      }
    }

    .publisher-info {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.75rem;
      color: ${({ theme }) => theme.textLight};
      font-size: 0.875rem;
      padding-top: 0.875rem;
      border-top: 1px solid ${({ theme }) => theme.borderLight};
      
      strong {
        color: ${({ theme }) => theme.text};
      }
    }

    .doi-link {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0;
      padding: 0.5rem 1rem;
      background: ${({ theme }) => theme.hover};
      color: ${({ theme }) => theme.accent};
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 600;
      transition: all 0.3s ease;
      border: 1px solid ${({ theme }) => theme.borderLight};

      &:hover {
        background: ${({ theme }) => theme.accent};
        color: white;
        border-color: ${({ theme }) => theme.accent};
        transform: translateY(-2px);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .publication-card {
      padding: 2rem;

      .pub-header {
        flex-direction: column;
        gap: 1rem;
      }
    }
  }
`;

function Publications() {
  const peerReviewedPublications = [
      {
      year: '2026',
      title: 'Random Projection Methods Outperform Principal Component Analysis for Dimensionality Reduction in Single Cell RNA-Seq',
      authors: ['Mohamed Abdelnaby', 'Marmar R. Moussa'],
      venue: 'Journal of Computational Biology (JCB)',
      type: 'Peer-Reviewed Publication',
      publisher: 'Journal of Computational Biology',
      doi: 'https://doi.org/10.1177/15578666261436821',
      publicationDate: 'April 27, 2026'
    },
    {
      year: '2025',
      title: 'A Benchmarking Study of Random Projections and Principal Components for Dimensionality Reduction Strategies in Single Cell Analysis',
      authors: ['Mohamed Abdelnaby', 'Marmar R. Moussa'],
      venue: 'Proceedings of the 13th International Conference on Computational Advances in Bio and Medical Sciences (ICCABS)',
      location: 'Georgia, USA',
      type: 'Peer-Reviewed Publication',
      publisher: 'Springer, Cham',
      series: 'Lecture Notes in Computer Science, vol 15599',
      isbn: {
        print: '978-3-032-02488-6',
        online: '978-3-032-02489-3'
      },
      doi: 'https://doi.org/10.1007/978-3-032-02489-3_1',
      publicationDate: 'November 1, 2025'
    }

  ];

  const positionPapers = [
    {
      year: '2024',
      title: 'Enhancing Inclusivity in Education through XR and AR with Cognitive-Affective Learning Principles for Students with Special Needs',
      authors: ['Mohamed Abdelnaby', 'et al.'],
      venue: '2024 ACM Conference on Human Factors in Computing Systems (CHI)',
      location: 'Honolulu, Hawaii, USA',
      type: 'Position Paper'
    }
  ];

  const researchPosters = [
    {
      year: '2025',
      title: 'Predicting T-Cell Receptor Specificity and Phenotype Using Integrated Classical and Pre-Trained Protein Language Models',
      authors: ['Marmar R. Moussa', 'Mohamed Abdelnaby'],
      venue: 'Proceedings of the 16th ACM International Conference on Bioinformatics, Computational Biology, and Health Informatics (ACM BCB)',
      location: 'Philadelphia, Pennsylvania, USA',
      type: 'Research Poster',
      publicationDate: 'October 12, 2025'
    },
    {
      year: '2024',
      title: 'Benchmarking Principal Component and Random Projection dimensionality reduction methods for single cell RNA sequencing data',
      authors: ['Mohamed Abdelnaby', 'Marmar R. Moussa'],
      venue: '2024 International Conference on Intelligent Systems for Molecular Biology (ISMB)',
      location: 'Montreal, Quebec, Canada',
      type: 'Research Poster'
    },
    {
      year: '2024',
      title: 'Random Projections techniques for locality-preserving representation of high-dimensional single cell RNA-seq',
      authors: ['Mohamed Abdelnaby', 'Marmar R. Moussa'],
      venue: '2024 International Conference on Research in Computational Molecular Biology (RECOMB)',
      location: 'Cambridge, MA, USA',
      type: 'Research Poster'
    },
    {
      year: '2024',
      title: 'Integrating Accelerometers & Neural Networks for Enhanced Soccer Analytics',
      authors: ['Mohamed Abdelnaby', 'et al.'],
      venue: '2024 University of Oklahoma – Tulsa REACH-OUT Poster Forum',
      location: 'Tulsa, OK, USA',
      type: 'Research Poster'
    }
  ];

  const renderPublication = (pub, index, cardClass = '') => (
    <motion.div 
      key={index} 
      className={`publication-card ${cardClass}`}
      initial={{ x: -30 }}
      whileInView={{ x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <div className="pub-header">
        <h3 className="pub-title">{pub.title}</h3>
        <span className="pub-year">{pub.year}</span>
      </div>
      <div className="authors">
        {pub.authors.map((author, authorIndex) => (
          <span key={authorIndex}>
            {author === 'Mohamed Abdelnaby' ? (
              <span className="highlight-author">{author}</span>
            ) : (
              author
            )}
            {authorIndex < pub.authors.length - 1 && ', '}
          </span>
        ))}
      </div>
      <div className="venue-info">
        <div className="venue">{pub.venue}{pub.location ? `; ${pub.location}` : ''}</div>
        <span className={`venue-badge ${cardClass}`}>{pub.type}</span>
      </div>
      
      {(pub.publisher || pub.status) && (
        <div className="publisher-info">
          {pub.publisher && (
            <span>
              <strong>Publisher:</strong> {pub.publisher}
              {pub.series && `, ${pub.series}`}
              {pub.publicationDate && <> &middot; <strong>Published:</strong> {pub.publicationDate}</>}
            </span>
          )}
          {pub.status && <span><strong>Status:</strong> {pub.status}</span>}
          {pub.doi && (
            <a href={pub.doi} target="_blank" rel="noopener noreferrer" className="doi-link" aria-label={`View publication: ${pub.title} (opens in new tab)`}>
              <FaExternalLinkAlt aria-hidden="true" /> View Publication
            </a>
          )}
        </div>
      )}
    </motion.div>
  );

  return (
    <PublicationsSection id="publications">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Publications & <span>Research</span>
        </motion.h2>

        <div className="publications-list">
          <div className="section-divider">
            <h3><span>Peer-Reviewed Publications</span></h3>
          </div>
          {peerReviewedPublications.map((pub, index) => renderPublication(pub, index, 'peer-reviewed'))}

          <div className="section-divider">
            <h3><span>Position Papers</span></h3>
          </div>
          {positionPapers.map((pub, index) => renderPublication(pub, index, 'position-paper'))}

          <div className="section-divider">
            <h3><span>Research Posters</span></h3>
          </div>
          {researchPosters.map((pub, index) => renderPublication(pub, index))}
        </div>
      </div>
    </PublicationsSection>
  );
}


const FooterContainer = styled.footer`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.body};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.accent}, transparent);
  }

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    .footer-text {
      font-size: 0.875rem;
      opacity: 0.8;
      text-align: center;
      line-height: 1.6;
    }

    .footer-links {
      display: flex;
      gap: 2rem;

      a {
        color: ${({ theme }) => theme.body};
        opacity: 0.7;
        transition: all 0.3s ease;
        font-size: 1.25rem;

        &:hover {
          opacity: 1;
          transform: translateY(-2px);
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <motion.div 
        className="footer-content"
        initial={{ y: 20 }}
        whileInView={{ y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <nav aria-label="Social media links">
          <div className="footer-links">
            <a href="https://www.linkedin.com/in/mabdelnaby1/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile (opens in new tab)">
              <FaLinkedin aria-hidden="true" />
            </a>
            <a href="https://github.com/MohamedHAbdelnaby" target="_blank" rel="noopener noreferrer" aria-label="GitHub profile (opens in new tab)">
              <FaGithub aria-hidden="true" />
            </a>
            <a href="https://x.com/iam_abdelnaby" target="_blank" rel="noopener noreferrer" aria-label="X profile (opens in new tab)">
              <FaXTwitter aria-hidden="true" />
            </a>
            <a href="mailto:mohamedabdelnaby@vt.edu" aria-label="Send email to mohamedabdelnaby@vt.edu">
              <FaEnvelope aria-hidden="true" />
            </a>
          </div>
        </nav>
        <div className="footer-text">
          © {new Date().getFullYear()} Mohamed Abdelnaby. All rights reserved.
        </div>
      </motion.div>
    </FooterContainer>
  );
}


const ResearchProjectsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};
  position: relative;

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.text};

    span {
      background: ${({ theme }) => theme.gradientText};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }
  }

  .section-intro {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.05rem;
    margin: 0 auto 3rem;
    max-width: 640px;
  }

  .research-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .research-project-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 20px;
    padding: 2.25rem;
    position: relative;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }
  }

  .project-meta {
    display: flex;
    gap: 0.85rem;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 0.85rem;
  }

  .project-badge {
    padding: 0.3rem 0.8rem;
    background: ${({ theme }) => theme.accent}1A;
    color: ${({ theme }) => theme.accent};
    border-radius: 50px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .project-year {
    color: ${({ theme }) => theme.textLight};
    font-size: 0.9rem;
    font-weight: 600;
  }

  .research-project-card h3 {
    font-size: 1.4rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    margin-bottom: 1rem;
    line-height: 1.35;
  }

  .research-project-card .description {
    color: ${({ theme }) => theme.textLight};
    line-height: 1.7;
    margin-bottom: 1.25rem;
    font-size: 0.98rem;
  }

  .tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
  }

  .tech-tag {
    padding: 0.35rem 0.85rem;
    background: ${({ theme }) => theme.hover};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 50px;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.textLight};
    font-weight: 500;
  }

  .research-links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.6rem 1.1rem;
      background: ${({ theme }) => theme.hover};
      border: 1px solid ${({ theme }) => theme.borderLight};
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      transition: all 0.3s ease;

      &:hover {
        background: ${({ theme }) => theme.accent};
        color: white;
        border-color: ${({ theme }) => theme.accent};
        transform: translateY(-1px);
      }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 2px;
      }

      @media (prefers-reduced-motion: reduce) {
        &:hover { transform: none; }
      }
    }
  }

  @media (max-width: 640px) {
    padding: 4rem 1rem;
    .research-project-card { padding: 1.5rem; }
    .research-project-card h3 { font-size: 1.2rem; }
  }
`;

function ResearchProjects() {
  const projects = [
    {
      title: "Random Projection Methods for Single-Cell RNA-Seq Dimensionality Reduction",
      year: "2026",
      badge: "Peer-Reviewed Paper · Journal of Computational Biology",
      description: "PCA is the default tool for scRNA-seq, but it scales poorly with dataset size, is sensitive to outliers, and assumes linearity. We benchmarked PCA against several random projection methods, including a Matching Sparsity Random Projection (MSRP) algorithm we introduce, which adapts projection density to the input's sparsity pattern. Across labeled and unlabeled datasets, RP methods matched or beat PCA on clustering quality and locality preservation, and ran substantially faster.",
      tech: ["Python", "scRNA-seq", "Random Projections", "PCA", "Clustering", "Bioinformatics"],
      links: [
        { type: "paper", url: "https://doi.org/10.1177/15578666261436821", label: "Read in JCB" },
        { type: "github", url: "https://github.com/moussa-lab/BenchmarkingPCA-RP", label: "GitHub" }
      ]
    },
    {
      title: "NeuroAgent: A Multi-Agent LLM Framework for Brain Imaging",
      year: "2026",
      badge: "Research Project · Live Demo",
      description: "A multi-agent LLM framework for 3D brain imaging. A unified five-agent architecture is deployed across multiple brain-imaging tasks and evaluated against five LLMs spanning 3B to 20B parameters from four families: Llama-3.2 (3B), Mistral (7B), Llama-3.1 (8B), Phi-4 (14B), and gpt-oss (20B). Ships with a live Streamlit demo for inspecting agent reasoning end-to-end.",
      tech: ["PyTorch", "FreeSurfer", "BrainLM", "BrainSegFounder", "LLM Orchestration", "RAG", "vLLM", "Streamlit"],
      links: [
        { type: "demo", url: "https://neuroagent.streamlit.app/", label: "Live Demo" },
        { type: "github", url: "https://github.com/MohamedHAbdelnaby/NeuroAgent", label: "GitHub" }
      ]
    },
    {
      title: "Loss-Aware Computing for Medical Image Restoration",
      year: "2026",
      badge: "Research Project",
      description: "Systematic loss-function characterization and GPU profiling framework for deep-learning-based low-dose CT image restoration. Spans six NVIDIA GPUs across five hardware generations: Pascal (P100), Volta (V100), Ampere consumer (RTX 3090), Ampere datacenter (A100), Hopper (H100), and Blackwell (RTX PRO 6000 Server Edition). Kernel-level analysis with NVIDIA Nsight Systems covers L2/HBM cache utilization, memory-hierarchy behavior, warp occupancy, and Roofline operational intensity across loss functions and batch sizes, paired with a formal Amdahl's Law treatment of how loss cost scales with backbone speedup on modern accelerators.",
      tech: ["PyTorch", "DDnet", "NVIDIA Nsight Systems", "SLURM", "HPC", "Roofline Analysis"],
      links: []
    },
    {
      title: "MuseumBot: RAG-Based Interactive Museum Companion",
      year: "2024",
      badge: "Research Project",
      description: "Interactive React Native museum application using a LLaMA 3.2 chatbot to deliver context-aware artwork information. Built on a FAISS-powered semantic search index over curated museum corpora, with a retrieval-augmented generation (RAG) architecture that grounds each response in retrieved passages.",
      tech: ["React Native", "LLaMA 3.2", "FAISS", "RAG", "Semantic Search"],
      links: []
    },
    {
      title: "Predictive Soccer Analytics: Computer Vision + Sensor Fusion",
      year: "2024",
      badge: "Research Project",
      description: "Computer vision system using OpenCV and convolutional neural networks to track soccer ball trajectories, combined with recurrent networks processing accelerometer time-series data. Sensor fusion across modalities was used to predict player injury risk from movement patterns.",
      tech: ["Python", "OpenCV", "CNN", "RNN", "Sensor Fusion"],
      links: []
    },
    {
      title: "Urban Scaling Analytics of the Roman Empire",
      year: "2022",
      badge: "Research Project",
      description: "Big-data analysis of 500,000+ Latin inscriptions to map urban scaling patterns across the Roman Empire. Built clustering and regression pipelines to quantify how socio-economic indicators scale with city size, applying modern computational methods to a historical dataset.",
      tech: ["Python", "Pandas", "Clustering", "Regression", "Big Data"],
      links: [
        { type: "github", url: "https://github.com/OU-DISC/urban_scaling", label: "GitHub" }
      ]
    }
  ];

  return (
    <ResearchProjectsSection id="research-projects" aria-labelledby="research-projects-heading">
      <div className="container">
        <motion.h2
          id="research-projects-heading"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Research <span>Projects</span>
        </motion.h2>
        <p className="section-intro">
          Selected deep-dive research work bridging systems, machine learning, and scientific computing.
        </p>
        <div className="research-list">
          {projects.map((project, index) => (
            <motion.article
              key={index}
              className="research-project-card"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="project-meta">
                <span className="project-badge">{project.badge}</span>
                <span className="project-year">{project.year}</span>
              </div>
              <h3>{project.title}</h3>
              <p className="description">{project.description}</p>
              <div className="tech-tags">
                {project.tech.map((t, i) => (
                  <span key={i} className="tech-tag">{t}</span>
                ))}
              </div>
              {project.links.length > 0 && (
                <div className="research-links">
                  {project.links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.type === 'github' ? <FaGithub /> : <FaExternalLinkAlt />}
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </ResearchProjectsSection>
  );
}


const SkillsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};
  position: relative;

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.text};

    span {
      background: ${({ theme }) => theme.gradientText};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }
  }

  .section-intro {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.05rem;
    margin: 0 auto 3rem;
    max-width: 600px;
  }

  .skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .skill-group {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 20px;
    padding: 1.75rem;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      border-color: ${({ theme }) => theme.accent}66;
      box-shadow: ${({ theme }) => theme.boxShadow};
    }
  }

  .skill-group h3 {
    font-size: 0.78rem;
    color: ${({ theme }) => theme.accent};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 1rem;
    font-weight: 700;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip {
    padding: 0.4rem 0.9rem;
    background: ${({ theme }) => theme.hover};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 50px;
    font-size: 0.85rem;
    font-weight: 500;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.accent};
      color: ${({ theme }) => theme.accent};
    }
  }

  @media (max-width: 640px) {
    padding: 4rem 1rem;
    .skill-group { padding: 1.4rem; }
  }
`;

function Skills() {
  const skillGroups = [
    {
      title: "Machine Learning & AI",
      skills: ["PyTorch", "scikit-learn", "Hugging Face", "vLLM", "LangChain", "LLMs", "Computer Vision", "Deep Learning"]
    },
    {
      title: "Systems & HPC",
      skills: ["CUDA", "NVIDIA Nsight Compute", "SLURM", "Parallel Computing", "Distributed Training", "Roofline Analysis"]
    },
    {
      title: "Languages",
      skills: ["Python", "C++", "C", "JavaScript", "MATLAB", "Bash"]
    },
    {
      title: "Scientific Tooling",
      skills: ["FastSurfer", "fMRIPrep", "NumPy", "Pandas", "SciPy", "Matplotlib"]
    },
    {
      title: "Web & Cloud",
      skills: ["React", "Node.js", "Firebase", "AWS", "Streamlit", "Docker"]
    },
    {
      title: "Dev Tools",
      skills: ["Git", "Linux", "VS Code", "Jupyter", "LaTeX", "Cursor"]
    }
  ];

  return (
    <SkillsSection id="skills" aria-labelledby="skills-heading">
      <div className="container">
        <motion.h2
          id="skills-heading"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Tools & <span>Technologies</span>
        </motion.h2>
        <p className="section-intro">
          The toolchain I use day-to-day across research, systems work, and applied projects.
        </p>
        <div className="skills-grid">
          {skillGroups.map((group, index) => (
            <motion.div
              key={group.title}
              className="skill-group"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              viewport={{ once: true }}
            >
              <h3>{group.title}</h3>
              <div className="chips">
                {group.skills.map((skill) => (
                  <span key={skill} className="chip">{skill}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </SkillsSection>
  );
}


const AwardsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}05 0%, transparent 70%);
    pointer-events: none;
  }

  .container {
    max-width: 1100px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.text};

    span {
      background: ${({ theme }) => theme.gradientText};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }
  }

  .section-intro {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.05rem;
    margin: 0 auto 3rem;
    max-width: 640px;
  }

  .awards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;
  }

  .award-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 20px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s ease;

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }
  }

  .award-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .award-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({ theme }) => theme.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  .award-year {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    margin: 0;
    line-height: 1.3;
  }

  .award-org {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.textLight};
    margin: 0;
  }

  .award-category {
    display: inline-block;
    align-self: flex-start;
    padding: 0.3rem 0.75rem;
    background: ${({ theme }) => theme.accent}1A;
    color: ${({ theme }) => theme.accent};
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    margin-top: 0.25rem;
  }

  @media (max-width: 600px) {
    padding: 3.5rem 1rem;
    .award-card { padding: 1.5rem; }
  }
`;

function Awards() {
  const awards = [
    {
      title: 'MIT Reality Hacks 2025',
      organization: 'MIT Media Lab',
      year: '2025',
      category: 'Hackathon Winner'
    },
    {
      title: 'Stanford Immerse the Bay 2024',
      organization: 'Stanford University',
      year: '2024',
      category: 'Hackathon Winner'
    }
  ];

  return (
    <AwardsSection id="awards" aria-labelledby="awards-heading">
      <div className="container">
        <motion.h2
          id="awards-heading"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Awards & <span>Recognitions</span>
        </motion.h2>
        <p className="section-intro">
          Selected honors and competitive wins recognizing my work in immersive
          technology and applied AI.
        </p>
        <div className="awards-grid">
          {awards.map((award, index) => (
            <motion.div
              key={index}
              className="award-card"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="award-header">
                <div className="award-icon" aria-hidden="true">
                  <FaTrophy />
                </div>
                <span className="award-year">{award.year}</span>
              </div>
              <h3>{award.title}</h3>
              <p className="award-org">{award.organization}</p>
              <span className="award-category">{award.category}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </AwardsSection>
  );
}


function App() {
  const [theme, setTheme] = useState('dark');
  const currentTheme = theme === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyles />
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Header toggleTheme={toggleTheme} theme={theme} />
      <main id="main-content" tabIndex="-1">
        <Hero />
        <News />
        <Publications />
        <ResearchProjects />
        <Projects />
        <Skills />
        <Research />
        <Experience />
        <About />
        <Awards />
      </main>
      <Footer />
    </ThemeProvider>
  );
}

export default App;
